import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { useState, useCallback, useRef, useMemo } from 'react';
import { Portal, Content } from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import styles from './Menu2.js';
import { usePortalTarget } from '../../components/UIProvider/PortalTargetContext.js';
import { useMenuContext, PaneContextProvider } from './MenuContext.js';
import '../../utils/index.js';
import { DATA_TESTID_SUFFIXES as DATA_TESTID_SUFFIXES$1 } from './constants.js';
import { DATA_TESTID_SUFFIXES } from '../Select/constants.js';
import MenuSearch from './MenuSearch.js';
import usePaneSearch from './Hooks/usePaneSearch.js';
import '../../hooks/index.js';
import useMutationObserver from './Hooks/useMutationObserver.js';
import useOverlay from '../../hooks/useOverlay/useOverlay.js';
import useCSSVariable from './Hooks/useCSSVariable.js';
import useSelectContext from '../Select/SelectContext.js';
import SelectedItems from '../Select/SelectedItems.js';
import PaneChildren from './PaneChildren.js';
import useFocusVirtualBoundary from './Hooks/useFocusVirtualBoundary.js';
import useIsVirtualized from './Hooks/useIsVirtualized.js';
import useMaskDimensions from './Hooks/useMaskDimensions.js';
import { useIsFocusVisible, conditionalA11yHelpersWrapper } from '../../components/UIProvider/A11yHelpersContext.js';
import useId from '../../hooks/useId/useId.js';
import getSubDataTestId from '../../utils/getSubDataTestId/getSubDataTestId.js';
import { useDidUpdate } from '../../hooks/useDidUpdate/useDidUpdate.js';
import mergeRefs from '../../utils/mergeRefs/mergeRefs.js';

const MenuPane = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  var _flyoutNode$parentNod, _process$env;
  let {
    placement,
    children,
    isSearchable,
    searchValue: externalSearchValue,
    onSearchValueChange,
    isSearchFilteringDisabled = false,
    className,
    zIndex = 1000,
    style,
    ...rest
  } = _ref;
  const [items, setItems] = useState([]);
  const [flyoutNode, setFlyoutNode] = useState(null);
  const onRefChange = useCallback(node => setFlyoutNode(node), []);
  const stickyBoxRef = useRef(null);
  const selectContext = useSelectContext(true);
  const withinSelect = selectContext !== null;
  const {
    multiselect,
    showSelectedItemsPane
  } = selectContext !== null && selectContext !== void 0 ? selectContext : {};
  const portalTarget = usePortalTarget();
  const [side, align] = useMemo(() => {
    var _placement$split, _placement$split2;
    return (_placement$split = placement === null || placement === void 0 || (_placement$split2 = placement.split) === null || _placement$split2 === void 0 ? void 0 : _placement$split2.call(placement, '-')) !== null && _placement$split !== void 0 ? _placement$split : [];
  }, [placement]);
  const thisPanesDepth = 1;
  const paneMaxHeight = useMemo(() => {
    let maxHeightString = '';
    const defaultPaneMaxHeight = 350;

    // check `style`
    if (style !== null && style !== void 0 && style.maxHeight) {
      if (typeof style.maxHeight === 'number') {
        return style.maxHeight;
      }
      if (typeof style.maxHeight === 'string') {
        maxHeightString = style.maxHeight;
      }
    }

    // check `className`
    if (!maxHeightString && flyoutNode) {
      const computedString = window.getComputedStyle(flyoutNode).maxHeight;
      if (computedString !== 'none') {
        maxHeightString = computedString;
      }
    }
    if (!maxHeightString) {
      return defaultPaneMaxHeight;
    }
    const maxHeightStringAsNumber = Number(maxHeightString);

    // if it's not suffixed
    if (!Number.isNaN(maxHeightStringAsNumber)) {
      return maxHeightStringAsNumber;
    }

    // if it is suffixed
    if (maxHeightString.slice(-2) === 'px' && !Number.isNaN(Number(maxHeightString.slice(0, -2)))) {
      return Number(maxHeightString.slice(0, -2));
    }
    return defaultPaneMaxHeight;
    // `className` impacts computed style
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style, className]);
  const paneBottomPadding = 4;
  const maskContainerId = useId();
  const maskContainer = document.getElementById(maskContainerId);
  const [transform, setTransform] = useState('');
  const target = (_flyoutNode$parentNod = flyoutNode === null || flyoutNode === void 0 ? void 0 : flyoutNode.parentNode) !== null && _flyoutNode$parentNod !== void 0 ? _flyoutNode$parentNod : undefined;
  const {
    dataTestId: rootDataTestId,
    openPaneDepth,
    maskHeight,
    open,
    triggerRef,
    searchBarConfigs,
    maskWidth,
    isVirtualizationDisabled
  } = useMenuContext();
  const isVirtualized = useIsVirtualized(children, isVirtualizationDisabled);
  const [isVirtualizationReady, setIsVirtualizationReady] = useState(typeof process !== 'undefined' && ((_process$env = process.env) === null || _process$env === void 0 ? void 0 : _process$env.NODE_ENV) === 'test');
  const animatedLeft = useMemo(() => openPaneDepth > thisPanesDepth, [openPaneDepth]);
  const dataTestId = getSubDataTestId(rootDataTestId, withinSelect ? DATA_TESTID_SUFFIXES.SELECT_PANE : DATA_TESTID_SUFFIXES$1.MENU_PANE);
  const {
    noSearchResultsNode,
    onInteractOutside,
    filteredChildren
  } = usePaneSearch({
    isSearchable,
    externalSearchValue,
    onSearchValueChange,
    items,
    animatedLeft,
    animatedRight: false,
    menuParentDataTestId: dataTestId,
    id: useId(),
    flyoutNode,
    children,
    isSearchFilteringDisabled,
    isVirtualized
  });
  const focusVirtualBoundary = useFocusVirtualBoundary(items);
  const {
    isFocusVisible
  } = useIsFocusVisible();
  const menuSearchInputRef = useRef(null);
  const maskIsReady = useMaskDimensions(flyoutNode, animatedLeft, thisPanesDepth, children, isVirtualized, isVirtualizationReady);
  useDidUpdate(() => {
    if (maskIsReady && isFocusVisible) {
      if (items[0]) {
        items[0].element.current.focus();
      } else if (menuSearchInputRef.current) {
        menuSearchInputRef.current.focus();
      }
    }
  }, [maskIsReady]);
  const defaultSide = useMemo(() => {
    var _triggerRef$current;
    const rect = triggerRef === null || triggerRef === void 0 || (_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.getBoundingClientRect();
    if (!rect) {
      return 'bottom';
    }
    const distanceToBottom = window.innerHeight - rect.bottom;
    const distanceToTop = rect.top;
    const paddedMaxHeight = paneMaxHeight + paneBottomPadding * 2;
    if (distanceToBottom > paddedMaxHeight) {
      return 'bottom';
    }
    if (distanceToTop > paddedMaxHeight) {
      return 'top';
    }
    return 'bottom';
  },
  // including `open` because the trigger location may change between openings
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [open, triggerRef]);

  // We use the top-level Radix container as a mask. Radix does not expose this container to consumers, except through CSS.
  // These hooks dynamically update the style of that container using CSS variables.
  useCSSVariable('--mask-z-index', String(zIndex), maskContainer);
  useCSSVariable('--mask-width', Number.isNaN(Number(maskWidth)) ? "".concat(maskWidth) : "".concat(maskWidth, "px"), maskContainer);
  useCSSVariable('--mask-height', Number.isNaN(Number(maskHeight)) ? "".concat(maskHeight) : "".concat(maskHeight, "px"), maskContainer);
  useCSSVariable('--flyout-max-height', "".concat(paneMaxHeight, "px"), maskContainer);
  useCSSVariable('--flyout-bottom-padding', "".concat(paneBottomPadding, "px"), maskContainer);
  useOverlay({
    isVisible: open,
    ignoreIds: [maskContainerId],
    opacity: 0
  });
  useMutationObserver(target, {
    attributes: true,
    attributeFilter: ['style']
  }, () => {
    var _style$transform, _style;
    return setTransform((_style$transform = target === null || target === void 0 || (_style = target.style) === null || _style === void 0 ? void 0 : _style.transform) !== null && _style$transform !== void 0 ? _style$transform : '');
  });
  return /*#__PURE__*/React__default.createElement(PaneContextProvider, {
    items: items,
    setItems: setItems,
    value: {
      dataTestId,
      isInteractive: !animatedLeft,
      flyoutNode,
      stickyBoxRef,
      zIndex,
      focusVirtualBoundary,
      isVirtualized,
      maskIsReady,
      setIsVirtualizationReady
    }
  }, /*#__PURE__*/React__default.createElement(Portal, {
    container: portalTarget || undefined
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.maskContainer,
    id: maskContainerId,
    style: {
      // virtuoso and radix both do funny things with window.requestAnimationFrame that cause unpredictable resizing.
      // this causes jitters. We need to wait until it's done resizing.
      opacity: maskIsReady ? 1 : 0
    }
  }, /*#__PURE__*/React__default.createElement(Content, _extends({}, rest, {
    style: style,
    onCloseAutoFocus: () => {
      var _triggerRef$current2;
      return triggerRef === null || triggerRef === void 0 || (_triggerRef$current2 = triggerRef.current) === null || _triggerRef$current2 === void 0 ? void 0 : _triggerRef$current2.focus();
    },
    sideOffset: 6,
    loop: !isSearchable,
    side: side || defaultSide,
    align: align || 'start',
    ref: mergeRefs(ref, onRefChange),
    "data-testid": dataTestId,
    className: classNames(className, {
      [styles.animatedLeft]: animatedLeft
    }, styles.flyout),
    onInteractOutside: onInteractOutside
  }), (isSearchable || multiselect && showSelectedItemsPane) && /*#__PURE__*/React__default.createElement("div", {
    className: classNames(styles.stickyBox, {
      [styles.isSearchable]: isSearchable
    }),
    ref: stickyBoxRef,
    style: {
      zIndex
    }
  }, showSelectedItemsPane && multiselect && /*#__PURE__*/React__default.createElement(SelectedItems, null)), noSearchResultsNode, /*#__PURE__*/React__default.createElement(PaneChildren, null, filteredChildren)), /*#__PURE__*/React__default.createElement("div", {
    className: styles.searchBoxesContainer,
    style: {
      transform,
      zIndex: zIndex + openPaneDepth,
      width: flyoutNode === null || flyoutNode === void 0 ? void 0 : flyoutNode.clientWidth
    }
  }, searchBarConfigs.length ? searchBarConfigs.map(_ref2 => {
    let {
      id,
      ...config
    } = _ref2;
    return /*#__PURE__*/React__default.createElement(MenuSearch, _extends({
      key: id
    }, config, {
      inputRef: menuSearchInputRef
    }));
  }) : null))));
});
var MenuPane$1 = conditionalA11yHelpersWrapper(MenuPane);

export { MenuPane$1 as default };
